/**
 * Inventory Transfer Report — what a project has had moved into it, and its value.
 *
 * A project manager who finds an item in stock transfers it to the project
 * warehouse in SAP. The report lists those movements for one project code: only
 * items actually transferred appear, so a sales order of ten items shows however
 * many were moved, not ten rows of zeroes.
 */
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/api';

const money = (n) => (n == null ? '' : Number(n).toLocaleString('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}));
const qty = (n) => (n == null ? '' : Number(n).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 4,
}));
const asDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');

export default function InventoryTransferReport() {
  const [projectCode, setProjectCode] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    const code = projectCode.trim();
    if (!code) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/reports/inventory-transfer?project_code=${encodeURIComponent(code)}`);
      setReport(data);
      setSearched(true);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not generate the report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  /** The client's own layout: the selection above, then the numbered lines. */
  const exportExcel = () => {
    if (!report?.rows?.length) return;
    const sheet = [
      ['Selection Option', 'Project Code', report.project_code],
      [],
      ...(report.project ? [
        ['Project Name', report.project.project_name || ''],
        ['Sales Order', report.project.sales_order || '', 'Customer', report.project.customer || ''],
        ['Posting Date', asDate(report.project.posting_date), 'Delivery Date', asDate(report.project.delivery_date)],
        [],
      ] : []),
      ['Sr.No.', 'Item Code', 'Item Description', 'Transfer Qty.', 'From WH', 'To WH', 'Rate', 'Total Value'],
      ...report.rows.map((r) => [
        r.sr_no, r.item_code, r.item_description, Number(r.transfer_qty),
        r.from_wh, r.to_wh,
        r.rate == null ? '' : Number(r.rate),
        r.total_value == null ? '' : Number(r.total_value),
      ]),
      [],
      ['', '', '', '', '', '', 'Total Transfer Value', Number(report.total_value || 0)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    ws['!cols'] = [{ wch: 8 }, { wch: 24 }, { wch: 40 }, { wch: 13 }, { wch: 32 }, { wch: 32 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Transfer');
    XLSX.writeFile(wb, `Inventory Transfer Report - ${report.project_code}.xlsx`);
  };

  return (
    <div className="space-y-3 pb-6">
      <div className="text-2xs text-sap-textLight">
        Main Menu › Inventory › <span className="text-sap-text font-medium">Inventory Transfer Report</span>
      </div>
      <h1 className="page-title">Inventory Transfer Report</h1>

      <form onSubmit={search} className="bg-white rounded-2xl shadow-sm p-4">
        <label className="block text-2xs font-semibold text-sap-textLight uppercase tracking-wide mb-1">
          Project Code
        </label>
        <div className="flex items-center gap-2">
          <input
            value={projectCode}
            onChange={(e) => setProjectCode(e.target.value)}
            placeholder="e.g. 262700749"
            className="w-80 px-3 py-1.5 text-xs border border-sap-border rounded-lg
              focus:outline-none focus:ring-1 focus:ring-sap-blue"
          />
          <button type="submit" disabled={loading || !projectCode.trim()}
            className="px-6 py-1.5 text-xs font-semibold rounded-lg bg-sap-blue text-white
              hover:bg-sap-blueDark transition-colors disabled:opacity-40">
            {loading ? 'Searching…' : 'Search'}
          </button>
          <button type="button" onClick={exportExcel}
            disabled={!report?.rows?.length}
            title={report?.rows?.length
              ? 'Download this report as an Excel file'
              : 'Search a project code with transfers first'}
            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white
              hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Export to Excel
          </button>
        </div>
        <p className="mt-1.5 text-2xs text-sap-textLight">
          Only items this project has actually had transferred are listed.
        </p>
      </form>

      {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}
      {report?.price_warning && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          {report.price_warning}
        </div>
      )}

      {!searched && !loading && (
        <div className="bg-white rounded-2xl shadow-sm py-16 text-center text-xs text-sap-textLight">
          Enter a project code to generate the Inventory Transfer Report.
        </div>
      )}

      {report && (
        <>
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs font-semibold text-sap-text mb-3">Project Information</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-xs">
              <Field label="Project Code" value={report.project?.project_code || report.project_code} />
              <Field label="Project Name" value={report.project?.project_name || '—'} />
              <Field label="Sales Order" value={report.project?.sales_order || '—'} highlight />
              <Field label="Customer" value={report.project?.customer || '—'} />
              <Field label="Posting Date" value={asDate(report.project?.posting_date)} />
              <Field label="Delivery Date" value={asDate(report.project?.delivery_date)} />
              <Field label="Status" value={report.project?.status || '—'} />
              <Field label="Generated By" value={report.generated_by || '—'} />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-sap-bg border-b border-sap-border">
                <tr className="text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold w-14">Sr.No.</th>
                  <th className="text-left px-3 py-2 font-semibold">Item Code</th>
                  <th className="text-left px-3 py-2 font-semibold">Item Description</th>
                  <th className="text-right px-3 py-2 font-semibold">Transfer Qty.</th>
                  <th className="text-left px-3 py-2 font-semibold">From WH</th>
                  <th className="text-left px-3 py-2 font-semibold">To WH</th>
                  <th className="text-right px-3 py-2 font-semibold">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-sap-textLight">
                      <div className="italic">
                        Nothing has been transferred for project {report.project_code} yet.
                      </div>
                      {report.blank_reason && (
                        <div className="mt-2 text-xs not-italic text-sap-text max-w-xl mx-auto">
                          {report.blank_reason}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : report.rows.map((r) => (
                  <tr key={`${r.transfer_doc_num}-${r.sr_no}`} className="border-t border-sap-border/30">
                    <td className="px-3 py-2 text-sap-textLight">{r.sr_no}</td>
                    <td className="px-3 py-2 font-medium text-sap-blue">{r.item_code}</td>
                    <td className="px-3 py-2 text-sap-text">{r.item_description}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{qty(r.transfer_qty)}</td>
                    <td className="px-3 py-2 text-sap-textLight">{r.from_wh}</td>
                    <td className="px-3 py-2 text-sap-textLight">{r.to_wh}</td>
                    <td className="px-3 py-2 text-right tabular-nums" title={r.rate_source || ''}>
                      {r.rate == null ? '—' : `₹${money(r.rate)}`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {r.total_value == null ? '—' : `₹${money(r.total_value)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
              {report.rows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-sap-border bg-sap-bg/60">
                    <td colSpan={7} className="px-3 py-2 text-right font-semibold text-sap-text">
                      TOTAL TRANSFER VALUE:
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-sap-blueDeeper tabular-nums">
                      ₹{money(report.total_value)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <p className="text-2xs text-sap-textLight uppercase tracking-wide">{label}</p>
      <p className={`mt-0.5 ${highlight ? 'text-sap-blue font-medium' : 'text-sap-text'}`}>{value}</p>
    </div>
  );
}
